import dayjs from 'dayjs';
import type {UseFormReturn} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRequiredLegend,
} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {Textarea} from '@/components/ui/textarea';
import {ToggleGroup, ToggleGroupItem} from '@/components/ui/toggle-group';
import {useAuthorize} from '@/hooks/useAuth';
import {EventSubType, EventType} from '@/services/graphqlHooks';
import ResidentInput, {
  ResidentMultiInput,
} from '@/shared/residents/components/ResidentInput';
import eventSubTypes from '@/translation/eventSubTypes';
import eventTypes from '@/translation/eventTypes';
import {TIME_FORMATS} from '@/utils/timeFormats';

import type {EventFormValues} from '../utils/eventsFormInputs';
import {
  eventTypesOptions,
  getEventSubTypeOptionsForSelectedType,
  getEventTypeClasses,
} from '../utils/eventsHelpers';

export type EventFormFieldsProps = {
  form: UseFormReturn<EventFormValues>;
  multipleResidents?: boolean;
};

const EventFormFields = ({
  form,
  multipleResidents = false,
}: EventFormFieldsProps) => {
  const {t} = useTranslation();

  const canUpdateEvent = useAuthorize('UPDATE_EVENT');

  return (
    <>
      <FormRequiredLegend />

      {canUpdateEvent && (
        <FormField
          control={form.control}
          name="type"
          render={({field}) => (
            <FormItem>
              <FormLabel>{t('Event type')}</FormLabel>
              <div>
                <FormControl>
                  <ToggleGroup
                    className="inline-flex"
                    type="single"
                    itemVariant="white"
                    onValueChange={(value: string) => {
                      form.setValue('eventSubType', EventSubType.None);
                      field.onChange(value);
                    }}
                    value={field.value}
                  >
                    {eventTypesOptions.map(eventType => (
                      <ToggleGroupItem
                        key={eventType}
                        value={eventType}
                        className={getEventTypeClasses(eventType).toggle}
                        aria-label={`Toggle ${
                          eventTypes[eventType] || eventType
                        }`}
                      >
                        {eventTypes[eventType] || eventType}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      {canUpdateEvent && form.watch('type') === EventType.Medical && (
        <FormField
          control={form.control}
          name="eventSubType"
          render={({field}) => (
            <FormItem>
              <FormLabel>{t('Event sub type')}</FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('Select event sub type')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {getEventSubTypeOptionsForSelectedType(
                      form.watch('type')
                    ).map(subType => (
                      <SelectItem key={subType} value={subType}>
                        {eventSubTypes[subType] || subType}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      <FormField
        control={form.control}
        name="date"
        render={({field}) => (
          <FormItem>
            <FormLabel required>{t('Date and time')}</FormLabel>
            <FormControl>
              <Input
                {...field}
                type="datetime-local"
                required
                value={dayjs(field.value || undefined).format(
                  TIME_FORMATS.dateTimeHTMLLocal
                )}
                onChange={evt => {
                  field.onChange(
                    dayjs(evt.target.value || undefined).toISOString()
                  );
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="pairingIds"
        render={({field}) => (
          <FormItem>
            <FormLabel>{t('Resident (optional)')}</FormLabel>
            <FormControl>
              {multipleResidents ? (
                <ResidentMultiInput
                  value={field.value}
                  onChange={field.onChange}
                />
              ) : (
                <ResidentInput
                  value={field.value[0] || ''}
                  onChange={pairingId =>
                    field.onChange(pairingId ? [pairingId] : [])
                  }
                />
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="content"
        render={({field}) => (
          <FormItem>
            <FormLabel required>{t('Description')}</FormLabel>
            <FormControl>
              <Textarea {...field} required />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};

export default EventFormFields;
